import type { ApiSwitchStoreResult } from '../../common/api/cart/responses/cartResponses';

import { getAuthTokenOrDefault } from '../../common/api/auth/getAuthToken';
import directCartApi from '../../common/api/cart/directCartApi';
import { directStoreApi } from '../../common/api/store/directStoreApi';
import { storeApi } from '../../common/api/store/storeApi';
import { createDefaultException } from '../../common/exceptions/exceptionUtils';
import { currentCartFlow, HOME_DELIVERY_MODE } from '../../common/flow/cart/currentCartFlow';
import { mapPointOfService } from '../../common/utils/storeUtils';

const getValidStoreIdsForPostalCode = async (postalCode: string) => {
    if (!postalCode) {
        return [];
    }

    const stores = await directStoreApi.getStoresByPostCode(postalCode);
    if (stores?.length > 0) {
        return stores.map((x) => x.code);
    }

    return [];
};

const ecomStoreFlow = {
    getPickupStores: async (query?: string, abortSignal?: AbortSignal) => {
        const response = await directStoreApi.getPointOfServices(query, abortSignal);
        const mapped = response.stores.map(mapPointOfService);
        return mapped;
    },
    changePickupStore: async (params: {
        pickupPointId: string;
        baseStoreId?: string;
        force: boolean; // means itll forcefully remove cart items that are not available in store
    }): Promise<ApiSwitchStoreResult> => {
        const { token, shoppingUserId } = await getAuthTokenOrDefault();

        const cartId = currentCartFlow.getCurrentCartId();

        if (!cartId) {
            return Promise.reject(createDefaultException('Kundvagn hittades inte', 'CartNotFound'));
        }

        const pickupStore = await directStoreApi.getPointOfService(params.pickupPointId);

        if (!pickupStore) {
            return Promise.reject(
                createDefaultException('Hämtbutik hittades inte', 'StoreNotFound'),
            );
        }

        const pickupMode = pickupStore.pickupDeliveryModes.find(Boolean);

        if (!pickupMode) {
            return Promise.reject(
                createDefaultException('Hämtbutik hittades inte', 'StoreNotValid'),
            );
        }

        const simulationResult = await directCartApi.changePointOfService(
            shoppingUserId,
            cartId,
            params.pickupPointId,
            params.force,
            token,
        );

        // store already changed, update pickup delivery since hybris requires it
        if (simulationResult.success && !simulationResult.simulated) {
            await directCartApi.setDeliveryMode(shoppingUserId, cartId, pickupMode.code, token);
        }

        return {
            forStoreId: pickupStore.name,
            success: simulationResult.success,
            simulated: simulationResult.simulated,
            removedCartItems: simulationResult.removedCartItems ?? [],
        };
    },
    changeDeliveryStore: async (params: {
        postalCode: string;
        baseStoreId?: string;
        force: boolean; // means itll forcefully remove cart items that are not available in store
    }): Promise<ApiSwitchStoreResult> => {
        const { token, shoppingUserId } = await getAuthTokenOrDefault();

        const postalCode = params.postalCode.replace(/\s/g, '');

        // seems we need to do this check, otherwise hybris does not return error but simulation: true so we dont know if its actually missing store or just product changes, and we expect forStoreId later aswell
        let forStoreId: string | undefined;

        if (params.baseStoreId) {
            const validStoreIds = await getValidStoreIdsForPostalCode(postalCode);
            if (validStoreIds.includes(params.baseStoreId)) {
                const store = await storeApi.fetchStoreById(params.baseStoreId);
                forStoreId = store?.id;
            }
        } else {
            const simpleStore = await directStoreApi.getStoreByPostCode(postalCode);
            forStoreId = simpleStore?.code;
        }

        if (!forStoreId) {
            return Promise.reject(createDefaultException('Butik hittades inte', 'StoreNotFound'));
        }

        const cartId = currentCartFlow.getCurrentCartId();

        if (!cartId) {
            return Promise.reject(createDefaultException('Kundvagn hittades inte', 'CartNotFound'));
        }

        const simulationResult = await directCartApi.changePostCode(
            shoppingUserId,
            cartId,
            postalCode,
            params.force,
            params.baseStoreId,
            token,
        );

        // store already changed, update home delivery since hybris requires it
        if (simulationResult.success && !simulationResult.simulated) {
            await directCartApi.setDeliveryMode(shoppingUserId, cartId, HOME_DELIVERY_MODE, token);
        }

        return {
            forStoreId,
            success: simulationResult.success,
            simulated: simulationResult.simulated,
            removedCartItems: simulationResult.removedCartItems ?? [],
        };
    },
};

export default ecomStoreFlow;
